import type * as rxjs from 'rxjs'

import type * as actions from '../actions'
import type { Feature } from '../feature'

/**
 * TODO:
 *
 * Producer can optionally return `undefined` to allow for effects that should
 * only be conditionally registered
 *
 * @example
 * store.effects.create((feature: MyFeature, number: number) => number % 2 ? ... : undefined)
 */
// NOTE 1: this is not in any namespace, as it is more discoverable at the top
// level
// NOTE 2: while we are not currently using generics in the return type here,
// this is still intentionally missing a return type in case we need generics
// in the future. Based on this:
// https://github.com/microsoft/TypeScript/issues/37835#issuecomment-638624108
export function create<
  TFeature extends Feature.Constraint,
  TArgs extends readonly unknown[],
>(
  producer: (
    feature: TFeature,
    ...args: TArgs
  ) => rxjs.Observable<actions.Action.Constraint | typeof SKIP> | undefined,
) {
  return (...args: TArgs) =>
    ((feature: TFeature) =>
      producer(feature, ...args)) satisfies Effect.Registrator<TFeature>
}

/**
 * Skips an emission in an effect
 */
// We could just use `undefined` or `void` instead, but this helps prevent
// forgetting to return something
export const SKIP = Symbol.for('@attain-apps/store.effects.SKIP')
/**
 * Alias for {@link SKIP}, but reads better in use cases where there is a manual
 * dispatch
 */
export const DISPATCHED_MANUALLY = SKIP

export type Effect = rxjs.Observable<actions.Action.Constraint | typeof SKIP>
export namespace Effect {
  export type Registrator<TFeature extends Feature.Constraint> = (
    feature: TFeature,
  ) => Effect | undefined
}
